/**
 * CampaignTimeline — the chronological event ribbon.
 *
 * Read directly from ``campaign_events``. No regeneration. Displays
 * newest-first with a subtle dot on a vertical thread. When the
 * campaign has a "waiting for X" state, the last row surfaces a
 * gentle next-step hint.
 */
import { Card } from "@/shared/ui/card";
import type { CampaignRoomPayload } from "../../types";

function formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}

export function CampaignTimeline({
  events,
}: {
  events: CampaignRoomPayload["timeline"];
}): JSX.Element {
  // Newest first for the timeline preview at the top of the room.
  const rows = [...events].reverse();

  return (
    <section data-testid="room-timeline">
      <div className="mb-6">
        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
          Timeline
        </p>
        <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight">
          What&apos;s happened so far
        </h2>
      </div>

      <Card className="p-4 sm:p-6">
        <ol className="relative border-l border-border pl-6 space-y-5">
          {rows.map((e) => (
            <li key={e.id} data-testid={`room-timeline-${e.event_name}`}>
              <span className="absolute -left-[7px] mt-1.5 h-3 w-3 rounded-full bg-primary" aria-hidden />
              <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4">
                <span className="text-xs text-muted-foreground tabular-nums sm:w-32">
                  {formatDate(e.occurred_at)} · {formatTime(e.occurred_at)}
                </span>
                <span className="font-medium">{e.display_label}</span>
              </div>
            </li>
          ))}
        </ol>
      </Card>
    </section>
  );
}
